<template>
    <BaseCard
        v-if="!suppressed && phase === 'dismissed'"
        theme="cream"
        radius="lgr"
        padding="none"
        elevated
        class="picker-card w-full mt-4 px-5 py-4"
        data-testid="chat-performance-picker-dismissed">
        <p class="scaffold-body-small text-valence-stone-500">No problem — just tell Nadia what you'd like to work on.</p>
    </BaseCard>
    <!-- Already picked a task on an earlier visit: stay out of the way, but keep a way back in. -->
    <BaseCard
        v-else-if="!suppressed && collapsed"
        theme="cream"
        radius="lgr"
        padding="none"
        elevated
        class="picker-card w-full mt-4"
        data-testid="chat-performance-picker-collapsed">
        <button
            type="button"
            class="flex w-full items-center gap-3 px-5 py-4 text-start transition-colors hover:bg-valence-cream-50 rounded-lgr"
            data-testid="picker-reopen"
            @click="expand">
            <span class="grow min-w-0 scaffold-body-small text-valence-stone-500 truncate">Your performance tasks</span>
            <span class="shrink-0 scaffold-body-small text-valence-stone-900">Show</span>
            <i class="bi bi-chevron-down text-xs/none shrink-0 text-valence-stone-900" aria-hidden="true"></i>
        </button>
    </BaseCard>
    <BaseCard
        v-else-if="!suppressed && (isFetching || hasMoments)"
        theme="cream"
        radius="lgr"
        padding="none"
        elevated
        class="picker-card w-full mt-4 flex flex-col px-5 pb-5.5"
        data-testid="chat-performance-picker">
        <div v-if="isFetching" class="flex items-center gap-3 py-5 text-valence-stone-500 scaffold-body-small">
            <LoadingSpinner class="w-4.5 h-4.5" />
            Loading your performance tasks…
        </div>
        <template v-else>
            <div class="flex items-center gap-3 pt-5 pb-3.5">
                <h2 class="nadia-chat-bold text-valence-stone-900 grow min-w-0 truncate">{{ phaseTitle }}</h2>
                <button type="button" aria-label="Close" class="text-valence-stone-900 hover:opacity-60 shrink-0 transition-opacity" data-testid="picker-close" @click="dismiss">
                    <i class="bi bi-x-lg text-sm/none" aria-hidden="true"></i>
                </button>
            </div>
            <PmPickerMomentPhase v-if="phase === 'moment'" :moments="moments" @select="selectMoment" @dismiss="dismiss" />
            <PmPickerTaskPhase
                v-else-if="phase === 'task' && moment"
                :self-reviews="selfReviews"
                :team-reviews="teamReviews"
                :self-label="selfLabel"
                :team-label="teamLabel"
                :show-empty-team-row="canAddReviewees"
                :current-conversation-hashid="currentConversationHashid"
                :is-busy="isLaunching"
                :start-disabled="isExpired"
                :preselected-cta-id="preselection?.cta_id"
                :preselected-team="preselection?.team"
                @row-clicked="handleRowClick"
                @open-team="openTeamPhase" />
            <PmPickerPersonPhase
                v-else-if="phase === 'person' && moment"
                :team-reviews="teamReviews"
                :can-add-relationships="pickerData?.can_add_relationships ?? false"
                :current-conversation-hashid="currentConversationHashid"
                :is-busy="isLaunching"
                :start-disabled="isExpired"
                :preselected-relationship-id="preselection?.relationship_id"
                :initial-search="preselection?.person_to_add"
                @row-clicked="handleRowClick"
                @person-added="refresh" />
            <div class="flex items-center gap-3 border-t border-valence-cream-200 pt-3.5 mt-1 scaffold-body-small">
                <button
                    v-if="canGoBack"
                    type="button"
                    class="flex min-w-0 shrink items-center gap-1.5 text-valence-stone-500 transition-colors hover:text-valence-stone-900"
                    data-testid="picker-back"
                    @click="goBack">
                    <i class="bi bi-chevron-left text-xs/none shrink-0" aria-hidden="true"></i>
                    <span class="min-w-0 truncate">{{ backLabel }}</span>
                </button>
                <a
                    :href="performancePageUrl"
                    class="ml-auto flex min-w-0 shrink-0 items-center gap-1.5 text-valence-stone-500 hover:text-valence-stone-900 transition-colors"
                    data-testid="picker-page-link"
                    @click="logPageLinkClick">
                    Go to the {{ performancePageLabel }} page
                    <i class="bi bi-arrow-up-right text-sm/none" aria-hidden="true"></i>
                </a>
            </div>
        </template>
        <BaseToast v-model:open="isErrorToastOpen" theme="error" :description="launchError" />
    </BaseCard>
</template>

<script setup lang="ts">
import type { OrganizationDetails } from "~vue/appTypes";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { usePerformancePicker } from "~vue/Moments/composables/usePerformancePicker";
import { useStartPerformanceReviewConversation } from "~vue/Moments/composables/useStartPerformanceReviewConversation";
import type { DecoratedPerformanceReview, PmPickerPreselection } from "~vue/types/performance_management";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, onMounted, type Ref, ref } from "vue";

import { ACTION } from "../chatActions";
import PmPickerMomentPhase from "./pmPicker/PmPickerMomentPhase.vue";
import PmPickerPersonPhase from "./pmPicker/PmPickerPersonPhase.vue";
import PmPickerTaskPhase from "./pmPicker/PmPickerTaskPhase.vue";

interface Props {
    // UIDirectiveRenderer contract: the action line plus message coordinates. Absent when
    // rendered as a mainline tool card via ChatMessageToolCall, which v-binds the toolCalls
    // map entry instead.
    data?: { action_name: string; action_params?: Record<string, unknown> };
    messageId?: number | string;
    lineIdx?: number;
    // Tool-card path: the persisted tool-result action line from the toolCalls map entry.
    // Its action_params carry the server-resolved `preselection` hints, and its action_state
    // carries `minimized` once the user has picked a task in an earlier visit.
    action?: { action_params?: Record<string, unknown>; action_state?: { minimized?: boolean } | null } | null;
}

const props = defineProps<Props>();

// The tool-card path spreads its whole map entry ({name, completed, action, ...}); keep
// those extras off the rendered DOM.
defineOptions({ inheritAttrs: false });

// Hashid of the coaching session this picker is rendered inside ("" in discovery chats).
const currentConversationHashid = inject<string>("coachingSessionId", "");

type ParsedPreselection = Omit<PmPickerPreselection, "matched">;

// Defensive parse: the preselection rides a persisted tool result, so treat it as untyped input.
// It only seeds the *initial* phase/selection — statuses always come from the live fetch.
function parsePreselection(raw: unknown): ParsedPreselection | null {
    if (!raw || typeof raw !== "object") {
        return null;
    }
    const record = raw as Record<string, unknown>;
    const parsed: ParsedPreselection = {
        moment_id: typeof record.moment_id === "number" ? record.moment_id : null,
        cta_id: typeof record.cta_id === "number" ? record.cta_id : null,
        team: record.team === true,
        relationship_id: typeof record.relationship_id === "string" ? record.relationship_id : null,
        person_to_add: typeof record.person_to_add === "string" ? record.person_to_add : null,
    };
    const hasAnyHint = parsed.moment_id !== null || parsed.cta_id !== null || parsed.team || parsed.relationship_id !== null || parsed.person_to_add !== null;
    return hasAnyHint ? parsed : null;
}

const preselection = parsePreselection(props.action?.action_params?.preselection);

// The tool found nothing the user can work in and no request for the picker itself, so this
// result has no card. Results persisted before the flag existed still render.
const suppressed = props.action?.action_params?.show_card === false;

type Phase = "moment" | "task" | "person" | "dismissed";
const phase = ref<Phase>("moment");
const selectedMomentId = ref<number | null>(preselection?.moment_id ?? null);

// Collapsed on arrival when a previous visit ended in a pick. Rendering the summary costs
// no fetch; the payload is only loaded if the user expands.
const collapsed = ref(!!props.action?.action_state?.minimized);

const { data: pickerData, isFetching, execute, decoratedReviews } = usePerformancePicker(selectedMomentId);

const moments = computed(() => pickerData.value?.moments ?? []);
const hasMoments = computed(() => moments.value.length > 0);
const moment = computed(() => pickerData.value?.moment ?? null);
const isExpired = computed(() => moment.value?.state === "expired");
const selectableMoments = computed(() => moments.value.filter((summary) => summary.state !== "locked"));

const selfReviews = computed(() => decoratedReviews.value.filter((review) => review.cta?.reviewer_target === "self"));
const teamReviews = computed(() => decoratedReviews.value.filter((review) => review.cta?.reviewer_target === "connection"));
// Section names mirror the PM page and the cycle tracker: org-configured labels win.
// Reviewees are added from the person phase, so that phase is worth opening on an empty team
// list — but only where the cycle asks for team reviews and the add controls are available.
const canAddReviewees = computed(() => !!pickerData.value?.can_add_relationships && (moment.value?.ctas ?? []).some((cta) => cta.reviewer_target === "connection"));

const selfLabel = computed(() => moment.value?.self_moment?.title || "Myself");
const teamLabel = computed(() => moment.value?.other_moment?.title || "My Team's Reviews");

const PHASE_TITLES: Record<Exclude<Phase, "dismissed">, string> = {
    moment: "Which moment would you like to work on?",
    task: "Where would you like to start?",
    person: "Who would you like to review?",
};
const phaseTitle = computed(() => {
    if (phase.value === "dismissed") {
        return "";
    }
    // The task phase is a specific moment's task list — name the moment so the user knows
    // where they are (the back link below carries the return path instead).
    if (phase.value === "task") {
        return moment.value?.title || PHASE_TITLES.task;
    }
    return PHASE_TITLES[phase.value];
});

const backLabel = computed(() => {
    if (phase.value === "person") {
        return moment.value?.title ? `Back to ${moment.value.title}` : "Back";
    }
    return "Back to all moments";
});

function initializePhase() {
    if (moment.value) {
        if (preselection?.relationship_id || preselection?.team) {
            phase.value = teamReviews.value.length || canAddReviewees.value ? "person" : "task";
            return;
        }
        if (preselection?.cta_id !== null && preselection?.cta_id !== undefined) {
            phase.value = "task";
            return;
        }
        if (preselection?.moment_id !== null && preselection?.moment_id !== undefined) {
            phase.value = "task";
            return;
        }
        // No hints: nothing to choose when there's a single selectable moment.
        if (selectableMoments.value.length <= 1) {
            phase.value = "task";
            return;
        }
    }
    phase.value = "moment";
}

// One impression event for every way the picker can appear: each wizard phase, and the
// collapsed summary on return visits. `is_reopen` marks the impression that followed a
// click on the collapsed summary, so first-open and re-open funnels stay separable.
function logImpression(loggedPhase: Phase | "collapsed", isReopen: boolean) {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_VIEWED, {
        moment_id: loggedPhase === "collapsed" ? selectedMomentId.value : moment.value?.id,
        phase: loggedPhase,
        preselected: !!preselection,
        is_reopen: isReopen,
    });
}

async function load(isReopen = false) {
    await execute();
    if (selectedMomentId.value === null) {
        selectedMomentId.value = pickerData.value?.default_moment_id ?? null;
    }
    initializePhase();
    logImpression(phase.value, isReopen);
}

onMounted(async () => {
    if (suppressed) {
        return;
    }
    // Arriving collapsed is itself an impression — and the only one this visit unless the
    // user expands, so it can't be inferred from the phase events.
    if (collapsed.value) {
        logImpression("collapsed", false);
        return;
    }
    await load();
});

// Re-opening is for this visit only: the persisted `minimized` stays set, so the next
// visit still opens collapsed rather than undoing the choice they already made.
async function expand() {
    collapsed.value = false;
    await load(true);
}

async function selectMoment(momentId: number) {
    selectedMomentId.value = momentId;
    phase.value = "task";
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_MOMENT_SELECTED, { moment_id: momentId });
    await execute();
}

// Phase moves that aren't a moment choice (which logs itself and implies the move forward).
function logPhaseChange(fromPhase: Phase, toPhase: Phase, direction: "forward" | "back") {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_PHASE_SELECTED, {
        moment_id: moment.value?.id,
        from_phase: fromPhase,
        to_phase: toPhase,
        direction,
    });
}

function openTeamPhase() {
    logPhaseChange(phase.value, "person", "forward");
    phase.value = "person";
}

const canGoBack = computed(() => {
    if (phase.value === "person") {
        return true;
    }
    return phase.value === "task" && selectableMoments.value.length > 1;
});

function goBack() {
    const target: Phase = phase.value === "person" ? "task" : "moment";
    logPhaseChange(phase.value, target, "back");
    phase.value = target;
}

function dismiss() {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_CLOSED, {
        moment_id: moment.value?.id,
        phase: phase.value,
    });
    reportClick({ type: "close", phase: phase.value, moment_id: moment.value?.id ?? null });
    phase.value = "dismissed";
}

// Escape hatch: the full PM page, opened at the currently selected cycle when there is one.
const performancePageUrl = computed(() => (selectedMomentId.value === null ? "/performance_management" : `/performance_management?moment_id=${selectedMomentId.value}`));

// Orgs can rename the PM page (perf_review_custom_copy.page_tab_label) — mirror the nav's label.
const organizationDetails = inject<Ref<OrganizationDetails | null> | undefined>("organization_details", undefined);
const performancePageLabel = computed(() => organizationDetails?.value?.perf_review_custom_copy?.page_tab_label || "Performance Management");

function logPageLinkClick() {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_PAGE_LINK_CLICKED, {
        moment_id: selectedMomentId.value,
        phase: phase.value,
    });
    reportClick({ type: "page_link", phase: phase.value, moment_id: selectedMomentId.value });
}

async function refresh() {
    await execute();
}

const { isLaunching, launchError, startConversation } = useStartPerformanceReviewConversation();

const isErrorToastOpen = ref(false);

// Clicks are persisted on the tool-result row so the session debug view can show what the
// user did with the card. Only the tool-card path has message coordinates to persist
// against, so without them this is a no-op. A row click navigates away and also collapses
// the card, so its event carries the minimized state for the local line update.
const globalProperties = inject<{ emitter?: { emit: (event: string, payload: unknown) => void } } | undefined>("globalProperties", undefined);

interface PickerClick {
    type: "row" | "close" | "page_link";
    phase: Phase;
    moment_id: number | null;
    cta_id?: number;
    task_label?: string;
    reviewer_target?: string;
    relationship_id?: string;
    person_name?: string;
    resumed?: boolean;
    session_hashid?: string;
}

function reportClick(click: PickerClick) {
    if (props.messageId === undefined || props.lineIdx === undefined) {
        return;
    }
    globalProperties?.emitter?.emit(ACTION.PM_PICKER, {
        message_id: props.messageId,
        lineIdx: props.lineIdx,
        subaction: "record_click",
        click,
        action_params: props.action?.action_params,
        ...(click.type === "row" ? { action_state: { minimized: true } } : {}),
    });
}

// The chat URL's trailing segment is the session hashid (chat:chat routes on it).
function sessionHashidFromUrl(url: string): string | undefined {
    return url.split("?")[0].split("/").filter(Boolean).pop();
}

function rowClick(review: DecoratedPerformanceReview, resumed: boolean, url: string): PickerClick {
    return {
        type: "row",
        phase: phase.value,
        moment_id: moment.value?.id ?? null,
        cta_id: review.performance_moment_cta_id,
        task_label: review.cta?.label,
        reviewer_target: review.cta?.reviewer_target,
        relationship_id: review.relationship?.id ? String(review.relationship.id) : undefined,
        person_name: review.relationship?.reviewee_name,
        resumed,
        session_hashid: sessionHashidFromUrl(url),
    };
}

const handleRowClick = async (review: DecoratedPerformanceReview) => {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_ROW_CLICKED, {
        moment_id: moment.value?.id,
        review_id: review.id,
        cta_id: review.performance_moment_cta_id,
        has_conversation: !!review.conversation,
        // The phase the click came from, and (for team reviews) who it was about — without
        // these, a self review and a team review are indistinguishable at the event level.
        phase: phase.value,
        reviewer_target: review.cta?.reviewer_target ?? null,
        relationship_id: review.relationship?.id ? String(review.relationship.id) : null,
    });

    // Resume/View: the conversation already exists — plain navigation, never reseed.
    if (review.conversation?.coaching_session_url) {
        reportClick(rowClick(review, true, review.conversation.coaching_session_url));
        openUrl(review.conversation.coaching_session_url);
        return;
    }

    // Expired cycles allow viewing existing conversations but never new starts (the backend
    // rejects them anyway).
    if (isExpired.value) {
        return;
    }

    const url = await startConversation(review);
    if (!url) {
        // A session-ended launch resolves null with an empty launchError: the modal dialog owns
        // that messaging, and a blank toast behind it would only add noise.
        if (launchError.value) {
            isErrorToastOpen.value = true;
        }
        return;
    }
    reportClick(rowClick(review, false, url));
    openUrl(url);
};
</script>

<style scoped>
.picker-card {
    animation: picker-in 0.28s ease-out both;
}

@keyframes picker-in {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
}

@media (prefers-reduced-motion: reduce) {
    .picker-card {
        animation: none;
    }
}
</style>
